import React, { useMemo, useRef } from 'react';
import { LiveDashboardPayload, LiveDashboardStudentRow } from '../../../hooks/v2/useLessonControlData';

interface TeacherLiveDashboardPanelProps {
  compact?: boolean;
  onInspectStudent?: (id: number) => void;
  dashboard: LiveDashboardPayload | null;
  loading: boolean;
  error: string | null;
  selectedBucket: string | null;
  onSelectBucket: (bucket: string) => void;
}

const bucketLabels: Record<string, string> = {
  needs_help: 'Needs Help',
  inactive: 'Not recently seen',
  behind: 'Different step',
  no_response: 'No Response',
  incorrect: 'Incorrect',
  missing_reasoning: 'Missing Reasoning',
  missing_confidence: 'Missing Confidence',
  working: 'Working',
  finished: 'Finished',
};

const bucketToneClasses: Record<string, { active: string; idle: string }> = {
  needs_help: {
    active: 'border-rose-300 bg-rose-100 text-rose-900',
    idle: 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100',
  },
  inactive: {
    active: 'border-amber-300 bg-amber-100 text-amber-900',
    idle: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
  },
  behind: {
    active: 'border-orange-300 bg-orange-100 text-orange-900',
    idle: 'border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100',
  },
  no_response: {
    active: 'border-sky-300 bg-sky-100 text-sky-900',
    idle: 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100',
  },
  incorrect: {
    active: 'border-red-300 bg-red-100 text-red-900',
    idle: 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100',
  },
  missing_reasoning: {
    active: 'border-yellow-300 bg-yellow-100 text-yellow-900',
    idle: 'border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100',
  },
  missing_confidence: {
    active: 'border-violet-300 bg-violet-100 text-violet-900',
    idle: 'border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100',
  },
  working: {
    active: 'border-slate-400 bg-slate-100 text-slate-900',
    idle: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
  },
  finished: {
    active: 'border-emerald-300 bg-emerald-100 text-emerald-900',
    idle: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
  },
};

const fallbackTone = {
  active: 'border-slate-400 bg-slate-100 text-slate-900',
  idle: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
};

const reasonLabels: Record<string, string> = {
  needs_help: 'Asked for help',
  inactive: 'Inactive or not joined',
  behind: 'On a different step',
  no_response: 'No response yet',
  incorrect: 'Incorrect response',
  missing_reasoning: 'Missing reasoning',
  missing_confidence: 'Missing confidence',
  working: 'Working',
  finished: 'Finished current step',
};

const recommendedActions: Record<string, string> = {
  needs_help: 'Check in now',
  inactive: 'Check context',
  behind: 'Check progress',
  no_response: 'Prompt for first answer',
  incorrect: 'Give quick reteach',
  missing_reasoning: 'Ask for one because/detail',
  missing_confidence: 'Have student choose confidence',
  working: 'Monitor',
  finished: 'Let continue or extend',
};

const labelForBucket = (bucket: string): string => (
  bucketLabels[bucket] || bucket.split('_').map((part) => (
    part ? `${part[0].toUpperCase()}${part.slice(1)}` : part
  )).join(' ')
);

const derivePresenceStatus = (row: LiveDashboardStudentRow): string => {
  if (!row.presence?.has_entered_live) return 'Not joined';
  if (row.presence?.inactive) return 'Inactive';
  if (row.presence?.recently_active) return 'Recently active';
  return 'Seen';
};

const simulationStatusDetail = (row: LiveDashboardStudentRow): string | null => {
  if (!row.is_simulation_activity) return null;
  const summary = row.simulation_summary;
  if (!summary) return 'Simulation not started';
  if (summary.status === 'needs_review') return summary.help_reason || 'Simulation state needs review';

  const actions = `${summary.action_count} action${summary.action_count === 1 ? '' : 's'}`;
  const evidence = `${summary.evidence_count} checkpoint${summary.evidence_count === 1 ? '' : 's'}`;
  if (summary.status === 'completed') return `Verified complete · ${evidence} · ${actions}`;
  if (summary.help_reason) return `${summary.help_reason} · ${evidence} · ${actions}`;
  if (summary.action_count === 0) return 'Simulation not started';
  const phase = summary.phase
    ? summary.phase.split('_').map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : part).join(' ')
    : 'In progress';
  return `${phase} · ${evidence} · ${actions}`;
};

const simulationRecommendedAction = (row: LiveDashboardStudentRow): string | null => {
  if (!row.is_simulation_activity) return null;
  const summary = row.simulation_summary;
  if (!summary) return 'Prompt to begin';
  if (summary.status === 'needs_review') return 'Check student device';
  if (summary.status === 'completed') return summary.support_used ? 'Review, then extend' : 'Offer extension';
  if (summary.safety_flag) return 'Check in on safety';
  if (summary.prompting_level > 0) return 'Give targeted support';
  if (summary.repeated_blocked_actions) return 'Give quick coaching';
  if (summary.action_count === 0) return 'Prompt to begin';
  return 'Monitor progress';
};

export const TeacherLiveDashboardPanel: React.FC<TeacherLiveDashboardPanelProps> = ({
  compact,
  onInspectStudent,
  dashboard,
  loading,
  error,
  selectedBucket,
  onSelectBucket,
}) => {
  const studentList = useRef<HTMLDetailsElement>(null);
  const priorityOrder = dashboard?.suggested_priority_order || [];
  const counts = dashboard?.bucket_counts || {};
  const resolvedBucket = selectedBucket || priorityOrder.find((bucket) => Number(counts[bucket] || 0) > 0) || priorityOrder[0] || '';
  const selectedRows = useMemo(
    () => (resolvedBucket && dashboard?.buckets?.[resolvedBucket] ? dashboard.buckets[resolvedBucket] : []),
    [dashboard, resolvedBucket],
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Classroom</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900">Student activity</h2>
        </div>
        {loading ? (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">Loading</span>
        ) : null}
      </div>

      {error ? (
        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {priorityOrder.length > 0 ? priorityOrder.filter(bucket => !compact || Number(counts[bucket] || 0) > 0 || bucket === 'needs_help').map((bucket) => {
          const selected = resolvedBucket === bucket;
          const tone = bucketToneClasses[bucket] || fallbackTone;
          return (
            <button
              key={`live-dashboard-bucket-${bucket}`}
              type="button"
              className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-sm transition ${
                selected ? tone.active : tone.idle
              }`}
              onClick={() => { onSelectBucket(bucket); if (studentList.current) studentList.current.open = true; }}
            >
              <span>{labelForBucket(bucket)}</span>
              <span className="font-semibold">{Number(counts[bucket] || 0)}</span>
            </button>
          );
        }) : (
          <span className="text-sm text-slate-500">Waiting for classroom dashboard data.</span>
        )}
      </div>

      <p className="mt-2 text-xs text-slate-500">Activity signals are observations, not engagement scores. Students may be working offline.</p>
      <details ref={studentList} className="mt-3"><summary className="cursor-pointer text-sm font-medium text-indigo-700">View students · {selectedRows.length} {resolvedBucket ? labelForBucket(resolvedBucket).toLowerCase() : ""}</summary><div className="mt-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            {resolvedBucket ? labelForBucket(resolvedBucket) : 'Students'}
          </p>
          <span className="text-xs text-slate-500">{selectedRows.length} student{selectedRows.length === 1 ? '' : 's'}</span>
        </div>
        <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {selectedRows.length > 0 ? selectedRows.map((row) => {
            const bucket = row.primary_bucket || resolvedBucket;
            const statusDetail = simulationStatusDetail(row);
            const recommendedAction = simulationRecommendedAction(row);
            return (
              <div key={`live-dashboard-student-${row.student_id}`} className="grid gap-2 px-3 py-2 text-sm md:grid-cols-[1.2fr_1fr_1fr_0.8fr] md:items-center">
                <div className="font-medium text-slate-900">{onInspectStudent ? <button className="text-left text-indigo-700 underline" onClick={() => onInspectStudent(row.student_id)}>{row.student_name}</button> : row.student_name}</div>
                <div className="text-slate-700">{statusDetail || reasonLabels[bucket] || labelForBucket(bucket)}</div>
                <div className="text-slate-700">{recommendedAction || recommendedActions[bucket] || 'Check status'}</div>
                <div className="text-xs font-medium text-slate-500">{derivePresenceStatus(row)}</div>
              </div>
            );
          }) : (
            <div className="px-3 py-3 text-sm text-slate-500">
              No students in this bucket.
            </div>
          )}
        </div>
      </div></details>
    </section>
  );
};
